import Box from '@mui/material/Box';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useLocation } from 'react-router-dom';
import WarningTooltip from '../../../components/WarningTooltip';

const ICON_BASE = `${process.env.PUBLIC_URL}/workbench/Workbench Tabs/Colored`;

const ADDITIONAL_TOOLS_TOOLTIP =
  'We’re building out this space with new integrations and features. Stay tuned for updates.';

export const TAB_KEYS = {
  MAIN: 0,
  FILES: 1,
  CALENDAR: 2,
  TASKS: 3,
  NOTEBOOK: 4,
  SLACK: 5,
  LINKS: 6,
  ADDITIONAL_TOOLS: 7,
};

const getTabItems = (isVertical) => [
  { value: TAB_KEYS.MAIN, label: 'Main', icon: 'Tab 1 - Main.svg' },
  {
    value: TAB_KEYS.FILES,
    label: isVertical ? 'Vertical Files' : 'My Files',
    icon: 'Tab 2 - My Folders.svg',
  },
  {
    value: TAB_KEYS.CALENDAR,
    label: isVertical ? 'Vertical Calendar' : 'My Calendar',
    icon: 'Tab 3 - My Calendar.svg',
  },
  { value: TAB_KEYS.TASKS, label: 'Task Manager', icon: 'Tab 4 - Tasks.svg' },
  { value: TAB_KEYS.NOTEBOOK, label: 'Notebook', icon: 'Tab 5 - Notebook.svg' },
  // Slack lives between Notebook and Links and only applies to the Vertical Workbench.
  ...(isVertical ? [{ value: TAB_KEYS.SLACK, label: 'Slack', iconSrc: '/slack.svg' }] : []),
  {
    value: TAB_KEYS.LINKS,
    label: isVertical ? 'Links' : 'My Links',
    icon: 'Tab 6 - My Links.svg',
  },
  // Additional Tools holds the Vertical Workbench's Notification Settings; it is
  // still "coming soon" on the Personal Workbench.
  {
    value: TAB_KEYS.ADDITIONAL_TOOLS,
    label: 'Additional Tools',
    icon: 'Tab 7 - Additional Tools.svg',
    ...(isVertical ? {} : { disabled: true, tooltip: ADDITIONAL_TOOLS_TOOLTIP }),
  },
];

const Tabs = ({ value, onChange, slackHasActivity = false }) => {
  const location = useLocation();
  const isVertical = location.pathname.endsWith('/workbench/vertical');
  const tabItems = getTabItems(isVertical);

  return (
    <Box sx={{ pb: 2 }}>
      <MuiTabs
        value={value}
        onChange={(_, newValue) => {
          if (tabItems.find((tab) => tab.value === newValue)?.tooltip) return;
          onChange(newValue);
        }}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Workbench tabs"
      >
        {tabItems.map((tab) => {
          // Red dot on the Slack tab when there's unseen workspace activity (Scenario 4).
          const showActivityDot = tab.value === TAB_KEYS.SLACK && slackHasActivity;
          const iconImg = (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <img
                src={tab.iconSrc ? tab.iconSrc : `${ICON_BASE}/${tab.icon}`}
                alt=""
                style={{ width: 24, height: 24 }}
              />
              {showActivityDot && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -7,
                    right: -7,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                  }}
                />
              )}
            </Box>
          );

          if (tab.tooltip) {
            // MuiTabs reads `value` off its direct child (this tooltip wrapper);
            // without it, it falls back to the child index, which collides with
            // the Links tab's value on the personal workbench.
            return (
              <WarningTooltip key={tab.value} value={tab.value} message={ADDITIONAL_TOOLS_TOOLTIP}>
                <Tab
                  value={tab.value}
                  label={tab.label}
                  icon={iconImg}
                  iconPosition="top"
                  disableRipple
                  sx={{ textTransform: 'none', minHeight: 64, cursor: 'not-allowed' }}
                />
              </WarningTooltip>
            );
          }

          return (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              icon={iconImg}
              iconPosition="top"
              sx={{
                textTransform: 'none',
                minHeight: 64,
                overflow: 'visible',
                '& .MuiTab-iconWrapper': { overflow: 'visible' },
              }}
            />
          );
        })}
      </MuiTabs>
    </Box>
  );
};

export default Tabs;
